// Shared EPCR tie-break: match points, then wins, then pool-stage points
// difference, then tries scored. From 2026/27, wins rank above points
// difference (Investec Champions Cup / EPCR Challenge Cup qualification
// rule change).
function compareByPointsWinsDiffTries(a, b) {
  if ((b?.points || 0) !== (a?.points || 0))
    return (b?.points || 0) - (a?.points || 0);
  if ((b?.won || 0) !== (a?.won || 0)) return (b?.won || 0) - (a?.won || 0);
  if ((b?.pointsDiff || 0) !== (a?.pointsDiff || 0))
    return (b?.pointsDiff || 0) - (a?.pointsDiff || 0);
  return (b?.triesFor || 0) - (a?.triesFor || 0);
}

// Teams finishing at a given pool position (1 = winner, 2 = runner-up, etc),
// ranked amongst themselves using the standard EPCR tiebreak.
export function rankTier(groups, tierPosition) {
  return (groups || [])
    .filter((row) => parseInt(row?.position, 10) === tierPosition)
    .sort(compareByPointsWinsDiffTries);
}

// Investec Champions Cup (4 pools): the 12 automatic qualifiers are seeded
// 1-12 in pool-position tiers (4 winners, 4 runners-up, 4 thirds). The
// remaining 12 teams are ranked 13-24 purely by points - seeds 13-16 are
// ICC's own Round of 16 wildcards, seeds 17-20 drop down into the EPCR
// Challenge Cup Round of 16, and seeds 21-24 are eliminated.
export function buildChampionsCupKnockout(groups) {
  let winners = rankTier(groups, 1);
  let runnersUp = rankTier(groups, 2);
  let thirds = rankTier(groups, 3);

  let automaticQualifiers = [...winners, ...runnersUp, ...thirds].map(
    (team, index) => ({ ...team, seed: index + 1, tier: tierLabel(index) })
  );

  let nonAutomatic = (groups || []).filter(
    (row) => parseInt(row?.position, 10) > 3
  );
  let wildcardRanking = [...nonAutomatic]
    .sort(compareByPointsWinsDiffTries)
    .map((team, index) => ({
      ...team,
      seed: automaticQualifiers.length + index + 1,
    }));

  // seeds 13-16
  let ownWildcards = wildcardRanking.slice(0, 4);
  // seeds 17-20
  let dropdownsToChallengeCup = wildcardRanking
    .slice(4, 8)
    .map((team) => ({ ...team, fromChampionsCup: true }));
  // seeds 21-24
  let eliminated = wildcardRanking.slice(8);

  return {
    automaticQualifiers,
    wildcardRanking,
    ownWildcards,
    dropdownsToChallengeCup,
    eliminated,
  };

  function tierLabel(index) {
    if (index < winners.length) return "Pool Winner";
    if (index < winners.length + runnersUp.length) return "Pool Runner-Up";
    return "Pool Third";
  }
}

// EPCR Challenge Cup (3 pools): seeds 1-8 are the best ECC qualifiers (3
// winners, 3 runners-up, the top 2 of the 3 third-placed teams), seeds 9-12
// are fixed for the 4 teams dropping down from the Investec Champions Cup,
// and seed 13 is the weakest of the 3 third-placed ECC teams. The remaining
// 9 ECC teams are ranked 14-22 purely by points - the top 3 of those (14-16)
// are ECC's own Round of 16 wildcards, the rest (17-22) are eliminated.
export function buildChallengeCupKnockout(groups, dropdowns) {
  let winners = rankTier(groups, 1);
  let runnersUp = rankTier(groups, 2);
  let thirds = rankTier(groups, 3);
  let bestThirds = thirds.slice(0, 2);
  let weakestThird = thirds.slice(2);

  let automaticQualifiers = [
    ...winners,
    ...runnersUp,
    ...bestThirds,
    ...(dropdowns || []),
    ...weakestThird,
  ].map((team, index) => ({
    ...team,
    seed: index + 1,
    tier: tierLabel(team, index),
  }));

  let nonAutomatic = (groups || []).filter(
    (row) => parseInt(row?.position, 10) > 3
  );
  let wildcardRanking = [...nonAutomatic]
    .sort(compareByPointsWinsDiffTries)
    .map((team, index) => ({
      ...team,
      seed: automaticQualifiers.length + index + 1,
      qualifying: index < 3,
    }));

  return { automaticQualifiers, wildcardRanking };

  function tierLabel(team, index) {
    if (team.fromChampionsCup) return "ICC Wildcard";
    if (index < winners.length) return "Pool Winner";
    if (index < winners.length + runnersUp.length) return "Pool Runner-Up";
    return "Pool Third";
  }
}

// Flattens a buildChampionsCupKnockout/buildChallengeCupKnockout result into
// a single team.id -> seed lookup.
export function toSeedMap({ automaticQualifiers, wildcardRanking }) {
  let seeds = new Map();
  [...(automaticQualifiers || []), ...(wildcardRanking || [])].forEach(
    (team) => {
      seeds.set(team?.team?.id, team?.seed);
    }
  );
  return seeds;
}
